import { AppIcon } from '@/components/ui/app-icon';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { APPSUMO_PLAN_CAPTION } from '@/lib/appsumo-upgrade';
import { currentPlanName } from '@/lib/credits';
import { iconArrowRight, iconBuilding, iconCheck } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { AvailablePlans, Plan } from '@/types';
import {
  appSumoPlanQuote,
  OWNED_BUTTON,
  pricingMoney,
  quotePricingV4,
  TICK_IN,
  type PricingPlanSelection,
} from './pricing-v4';
import { siteUrl } from '@/lib/brand';

interface Props {
  catalog: AvailablePlans;
  interval: 'month' | 'year';
  disabled?: boolean;
  processingPriceId?: string | null;
  onCheckout: (priceId: string, details: PricingPlanSelection) => void;
  trialActive?: boolean;
  hasNewFeatureAccess?: boolean;
  organizationId?: string;
  /** Opens a separate customize step instead of checking out from the card,
   *  which then drops its credits picker. */
  onChoose?: (plan: Plan) => void;
}

/** Pro is the one we point at, matching the marketing page. */
const RECOMMENDED_TIER = 'pro';

const tierLabel = (plan: Plan) =>
  plan.tier_key
    ? plan.tier_key[0].toUpperCase() + plan.tier_key.slice(1)
    : plan.name;

/** Where the marketing site's "Talk to sales" goes. */
const TALK_TO_SALES_URL = siteUrl('https://cleanlist.ai/book');

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className='flex gap-2.5'>
      <AppIcon
        icon={iconCheck}
        className='mt-[3px] h-3 w-3 shrink-0 text-primary'
      />
      <span className='text-[13px] leading-snug text-muted-foreground'>
        {children}
      </span>
    </li>
  );
}

function PricingCard({
  plan,
  includes,
  ...props
}: Props & {
  plan: Plan;
  /** The tier before this one, whose features this one has too. */
  includes?: string;
}) {
  const current =
    props.catalog.current_plan?.id === plan.id
      ? props.catalog.current_plan
      : undefined;
  // Every size shares one per-credit price, so the current add-on is matched
  // by its credits; the price id is the fallback for an API that predates it.
  const currentCredits =
    current?.credit_addon_credits ??
    plan.credit_options?.find(
      (option) =>
        option.monthly_price_id === current?.credit_addon_price_id ||
        option.yearly_price_id === current?.credit_addon_price_id
    )?.credits ??
    0;
  const [extraCredits, setExtraCredits] = useState(currentCredits);
  const addon = plan.credit_options?.find(
    (option) => option.credits === extraCredits
  );
  const addonSizes = (plan.credit_options ?? [])
    .map((option) => option.credits)
    .sort((a, b) => a - b);
  const stops = [0, ...addonSizes];
  const quote = quotePricingV4(
    plan,
    props.interval,
    props.catalog.seats_used,
    addon
  );
  const free = plan.tier_key === 'free';
  const label = tierLabel(plan);
  const recommended = plan.tier_key === RECOMMENDED_TIER;
  // Free is never badged: a trial keeps the org on Free until it picks a
  // plan, so "Current" there would contradict the Pro trial it's on.
  const isCurrent = !free && props.catalog.current_plan?.id === plan.id;
  // A trial can still start now; another billing period or credit amount is
  // an update. Only the exact plan already held has nothing to buy.
  const changing = isCurrent && !props.trialActive;
  const owned =
    changing &&
    props.interval ===
      (props.catalog.current_billing_interval ?? props.interval) &&
    extraCredits === currentCredits;
  // The headline is the per-seat rate, the way the marketing site quotes it.
  const base =
    props.interval === 'year' ? plan.yearly_price : plan.monthly_price;
  const perSeatMonthly = base
    ? props.interval === 'year'
      ? base.price / 12
      : base.price
    : null;
  const cadence = props.interval === 'year' ? 'annually' : 'monthly';
  // The plan held at the AppSumo price: its own seat price and monthly
  // total, and no credits of its own (only an add-on's, if any).
  const heldAddonCents =
    plan.credit_options?.find((option) => option.credits === currentCredits)
      ?.monthly_price_cents ?? 0;
  const appSumo = appSumoPlanQuote(
    plan,
    current,
    props.interval,
    heldAddonCents / 100
  );
  const shownCredits = appSumo
    ? appSumo.seats * appSumo.creditsPerSeat + (addon?.credits ?? 0)
    : quote.credits;

  return (
    <section
      className={cn(
        'flex flex-col rounded-xl border bg-card p-5',
        recommended
          ? 'border-primary/40 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(62,139,255,0.35)]'
          : 'border-border'
      )}
    >
      <div className='flex flex-wrap items-center gap-2'>
        <h3 className='text-lg font-semibold'>{label}</h3>
        {recommended && (
          <span className='rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'>
            Recommended
          </span>
        )}
        {isCurrent && (
          <span className='rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'>
            Current
          </span>
        )}
      </div>
      {plan.description && (
        <p className='mt-1 text-sm text-muted-foreground'>{plan.description}</p>
      )}

      <p className='mt-5 flex items-baseline gap-1'>
        <span className='text-3xl font-semibold tracking-tight'>
          {free
            ? '$0'
            : appSumo
              ? pricingMoney(appSumo.perSeatMonthly)
              : perSeatMonthly == null
                ? 'Unavailable'
                : pricingMoney(perSeatMonthly)}
        </span>
        {appSumo?.listPerSeatMonthly != null && (
          <s className='text-base font-normal text-muted-foreground tabular-nums'>
            {pricingMoney(appSumo.listPerSeatMonthly)}
          </s>
        )}
        <span className='text-sm font-normal text-muted-foreground'>
          {free ? '/ month' : '/ seat / month'}
        </span>
      </p>
      {appSumo && (
        <p className='mt-1 text-xs font-medium text-foreground'>
          {APPSUMO_PLAN_CAPTION}
        </p>
      )}
      {/* The line under the price: Free's as the site words it, and a paid
          plan's monthly price for the org, its seats plus any credit add-on
          (the credits box counts those). */}
      <p className='mt-1 text-xs text-muted-foreground'>
        {free
          ? 'Free forever'
          : appSumo
            ? `${pricingMoney(appSumo.total)} a month for your ${appSumo.seats} seat${appSumo.seats === 1 ? '' : 's'}, billed ${cadence}`
            : quote.monthlyEquivalent != null
              ? `${pricingMoney(quote.monthlyEquivalent)} a month for your ${quote.seats} seat${quote.seats === 1 ? '' : 's'}, billed ${cadence}`
              : `Billed ${cadence}`}
      </p>

      {!free && (
        <>
          {/* A plan at the AppSumo price brings no credits of its own. */}
          {appSumo && shownCredits === 0 ? null : (
            <div className='mt-4 rounded-lg bg-muted/40 px-3 py-2.5'>
              <p className='text-[13px] font-medium'>
                <span key={shownCredits} className={TICK_IN}>
                  {shownCredits.toLocaleString()}
                </span>{' '}
                shared credits / month
              </p>
            </div>
          )}
          {addonSizes.length > 0 && !props.onChoose && (
            <div className='mt-4'>
              <div className='flex items-baseline justify-between gap-2 text-xs'>
                <span className='font-medium'>Extra workspace credits</span>
                <span
                  key={extraCredits}
                  className={cn(
                    TICK_IN,
                    'tabular-nums',
                    addon ? 'font-semibold' : 'text-muted-foreground'
                  )}
                >
                  {addon ? `+${addon.credits.toLocaleString()}` : 'None'}
                </span>
              </div>
              <Slider
                className='mt-3'
                min={0}
                max={addonSizes.length}
                showSteps
                smooth
                value={[Math.max(0, stops.indexOf(extraCredits))]}
                onValueChange={([index]) => setExtraCredits(stops[index] ?? 0)}
                disabled={props.disabled}
                aria-label='Extra workspace credits'
                aria-valuetext={
                  addon
                    ? `${addon.credits.toLocaleString()} extra credits`
                    : 'No extra credits'
                }
              />
              <div className='mt-1.5 flex justify-between text-[11px] text-muted-foreground tabular-nums'>
                <span>None</span>
                <span>
                  +{addonSizes[addonSizes.length - 1].toLocaleString()}
                </span>
              </div>
              <p className='mt-2 text-xs text-muted-foreground'>
                One add-on for the whole workspace, regardless of seats.
              </p>
            </div>
          )}
        </>
      )}

      {includes && (
        <p className='mt-5 text-xs font-medium text-muted-foreground'>
          Everything in {includes}, plus:
        </p>
      )}
      <ul className={cn('mb-5 flex-1 space-y-2', includes ? 'mt-3' : 'mt-5')}>
        {plan.features.map((feature) => (
          <Feature key={feature}>{feature}</Feature>
        ))}
      </ul>

      {free ? (
        <p className='text-sm text-muted-foreground'>
          Free access remains available after cancellation.
        </p>
      ) : (
        <>
          <Button
            variant={owned || !recommended ? 'outline' : 'default'}
            className={owned ? OWNED_BUTTON : undefined}
            disabled={
              owned ||
              props.disabled ||
              !quote.available ||
              props.processingPriceId != null
            }
            onClick={() => {
              if (props.onChoose) return props.onChoose(plan);
              if (!quote.priceId) return;
              props.onCheckout(quote.priceId, {
                id: plan.id,
                name: plan.name,
                seats: quote.seats,
                price: quote.total ?? undefined,
                credits: quote.credits,
                credit_addon_price_id: quote.addonPriceId,
                credit_addon_credits: addon?.credits,
              });
            }}
          >
            {owned
              ? "You're on this plan"
              : props.processingPriceId === quote.priceId
                ? 'Opening checkout...'
                : changing
                  ? 'Update plan'
                  : props.trialActive
                    ? `Start ${label} now`
                    : `Choose ${label}`}
          </Button>
        </>
      )}
    </section>
  );
}

/** Enterprise has no self-serve price, so it gets a band of its own, built
 *  from the cards' pieces. Its headline and list come from the billing
 *  catalog, which mirrors the marketing site; the line under the headline is
 *  the site's too. */
function EnterpriseBand({ plan }: { plan: Plan }) {
  return (
    <section className='grid gap-6 rounded-xl border border-border bg-linear-to-br from-card to-muted/40 p-5 @2xl:grid-cols-2 @2xl:items-center @2xl:gap-10 @2xl:p-6'>
      <div>
        <div className='flex items-center gap-2.5'>
          <span className='grid size-8 place-items-center rounded-lg bg-muted text-foreground'>
            <AppIcon icon={iconBuilding} className='h-3.5 w-3.5' />
          </span>
          <h3 className='text-lg font-semibold'>Enterprise</h3>
        </div>
        <p className='mt-5 text-2xl font-semibold tracking-tight text-balance'>
          {plan.description ?? 'Unlimited agents, priced to your volume.'}
        </p>
        <p className='mt-2 max-w-md text-sm leading-relaxed text-muted-foreground'>
          Credit volume sized to your pipeline, with a named success manager
          from day one.
        </p>
        <div className='mt-5 flex flex-wrap gap-2'>
          <Button asChild>
            <a
              href={TALK_TO_SALES_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              Talk to sales
              <AppIcon icon={iconArrowRight} />
            </a>
          </Button>
          <Button variant='outline' asChild>
            <a href='mailto:support@cleanlist.ai'>Email us</a>
          </Button>
        </div>
      </div>
      {plan.features.length > 0 && (
        <div className='@container rounded-lg bg-muted/40 p-4 @2xl:p-5'>
          <p className='text-xs font-medium text-muted-foreground'>
            Everything in Pro, plus:
          </p>
          <ul className='mt-3 grid gap-x-6 gap-y-2.5 @sm:grid-cols-2'>
            {plan.features.map((feature) => (
              <Feature key={feature}>{feature}</Feature>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** Pricing, feature copy and available options come from the billing catalog. */
export function PricingV4Cards(props: Props) {
  const current = props.catalog.current_plan;
  // A Free workspace has no price to keep. The legacy Free plan is "Free Tier"
  // with no tier_key, so the name is checked as well.
  const grandfathered =
    !props.hasNewFeatureAccess &&
    current &&
    current.pricing_version !== 'v4' &&
    current.tier_key !== 'free' &&
    !/^free\b/i.test(current.name);
  const enterprise = props.catalog.plans.find(
    (plan) => plan.tier_key === 'enterprise'
  );
  const selfServe = props.catalog.plans.filter(
    (plan) => plan.tier_key !== 'enterprise'
  );
  return (
    <div className='@container space-y-4'>
      {grandfathered && (
        <div className='rounded-lg border border-border bg-muted/30 p-4 text-sm'>
          You&apos;re on the {currentPlanName(props.catalog)} plan. Your price
          and credits stay the same.
          <p className='mt-1 text-muted-foreground'>
            Switch to new pricing below to access the new Agents, Sequences and
            Slack Clu features included in your chosen plan.
          </p>
        </div>
      )}
      {/* Columns follow this component's width, not the window's: the Clu
          dock or the settings frame can leave it narrow on a wide screen. */}
      <div className='grid grid-cols-1 gap-4 @2xl:grid-cols-3'>
        {selfServe.map((plan, index) => (
          <PricingCard
            key={`${props.organizationId ?? ''}:${plan.id}:${props.catalog.current_plan?.credit_addon_price_id ?? ''}`}
            plan={plan}
            includes={index > 0 ? tierLabel(selfServe[index - 1]) : undefined}
            {...props}
          />
        ))}
      </div>
      {enterprise && <EnterpriseBand plan={enterprise} />}
    </div>
  );
}
